import { Action, createReducer, on } from '@ngrx/store';

import { PackagesActions } from 'src/app/features/packages/store/actions';

import { PackageUsersActions } from '../actions';
import { PackageUsersState } from '../state';
import { CheckInStatus, PackageUser } from '../../models';

const reducer = createReducer(
  PackageUsersState.initialState,
  on(PackageUsersActions.SetActivePackageUser, (state: PackageUsersState.State, { payload }) => ({
    ...state,
    activePackageUser: payload.packageUser,
  })),
  on(
    PackageUsersActions.SetActivePackageUserGuid,
    (state: PackageUsersState.State, { payload }) => ({
      ...state,
      activePackageUserGuid: payload.activePackageUserGuid,
    })
  ),
  on(
    PackageUsersActions.SetActivePackageAndUserGuids,
    (state: PackageUsersState.State, { payload }) => ({
      ...state,
      activePackageUserGuid: payload.activePackageUserGuid,
    })
  ),
  on(
    PackageUsersActions.FetchPackageUsersSuccessful,
    (state: PackageUsersState.State, { payload }) => ({
      ...state,
      packageUsers: payload.packageUsers,
    })
  ),
  on(PackageUsersActions.ResetPackageUsers, (state: PackageUsersState.State) => ({
    ...state,
    packageUsers: [],
  })),
  on(PackagesActions.ValidatePackageSuccessful, (state: PackageUsersState.State, { payload }) => ({
    ...state,
    isSigningAgent: payload.isUserSigningAgent,
  })),
  on(
    PackageUsersActions.SetPackageUserCheckInStatus,
    (state: PackageUsersState.State, { payload }) => ({
      ...state,
      packageUsers: state.packageUsers?.map((pu) =>
        pu.packageUserGuid === payload.packageUserGuid
          ? { ...pu, checkInStatus: payload.checkInStatus }
          : pu
      ),
    })
  ),
  on(
    PackageUsersActions.SetActivePackageUserDashboardUrl,
    (state: PackageUsersState.State, { payload }) => ({
      ...state,
      activePackageUserDashboardUrl: payload.dashboardUrl,
    })
  ),
  on(
    PackageUsersActions.PackageUserCheckInStatusUpdated,
    (state: PackageUsersState.State, { payload }) => ({
      ...state,
      packageUsers: state.packageUsers?.map((pu) =>
        pu.packageUserGuid === payload.packageUserGuid
          ? { ...pu, checkInStatus: mergeCheckInStatus(pu, payload.checkInStatus) }
          : pu
      ),
    })
  )
);

// Two SignalR paths write the same checkInStatus slot: real ClearSign check-in and eSign
// presign progress (which arrives with esignStep set). Before check-in starts, a genuine
// check-in update reports "Not Started" - letting it overwrite drops the in-flight eSign
// step and flickers the Signing Room to "Check-in not started". Preserve the eSign step
// until check-in actually starts, at which point the incoming payload wins.
function mergeCheckInStatus(
  packageUser: PackageUser,
  incoming: CheckInStatus
): CheckInStatus {
  if (incoming.isStarted || incoming.esignStep) {
    return incoming;
  }

  const existing = packageUser.checkInStatus;
  if (!existing?.esignStep) {
    return incoming;
  }

  return {
    ...incoming,
    esignStep: existing.esignStep,
    esignStepStatus: existing.esignStepStatus,
  };
}

export function packageUsersReducer(state: PackageUsersState.State | undefined, action: Action) {
  return reducer(state, action);
}
